<template>
    <el-dialog
        :title="dialogTitle"
        :visible.sync="localVisible"
        :close-on-click-modal="false"
        :width="isRemoteBreakdown ? '100%' : '60%'"
        size="small"
        class="text-left"
        @close="handleClose"
    >

        <div class="p-2" v-loading="remoteLoading">
            <div v-if="row" class="item-header">
                <div class="item-photo">
                    <img v-if="photoUrl" :src="photoUrl" :alt="row.series" />
                    <span v-else class="text-muted small">No photo</span>
                </div>
                <div class="item-details">
                    <div class="item-name">{{ row.name || '-' }}</div>
                    <div><span class="item-label">SKU</span> {{ row.series }}</div>
                </div>
            </div>

            <div v-if="remoteError" class="text-danger small mb-2">{{ remoteError }}</div>
            <div v-else-if="isRemoteBreakdown && !remoteLoading && remoteRows.length === 0" class="text-muted small mb-2">
                No breakdown details found.
            </div>

            <ag-grid-vue
                ref="model"
                class="ag-theme-alpine cell-dialog-grid"
                style="width: 100%; height: 400px"
                :columnDefs="activeColumnDefs"
                :rowData="activeRowData"
                :pinnedBottomRowData="activePinnedBottomRowData"
                :enableSorting="true"
                :enableFilter="true"
                :defaultColDef="{
                    filter: true,
                    sortable: true,
                    flex: 1,
                    resizable: true
                }"
                :rowHeight="40"
                :context="{ vm: this }"
                @grid-ready="onGridReady"
                @cell-context-menu="handleGridCellContextMenu"
                @contextmenu.native.prevent="suppressBrowserContextMenu"
            />

            <div
                v-if="contextMenu.visible"
                class="cell-dialog-context-menu"
                :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
                @click.stop
                @contextmenu.prevent
            >
                <div class="cell-dialog-context-menu-location">
                    Row {{ contextMenu.rowIndex }} · {{ contextMenu.header }}
                </div>
                <button type="button" @click="copyContextCell">Copy</button>
                <button type="button" @click="copyContextColumn">Copy Column</button>
                <button type="button" @click="copyContextRow">Copy Row</button>
                <button type="button" @click="copyContextTable">Copy Table</button>
            </div>
        </div>

        <span slot="footer" class="dialog-footer">
            <el-button size="small" @click="localVisible = false">Close</el-button>
        </span>
    </el-dialog>
</template>

<script>
import 'ag-grid-community/dist/styles/ag-grid.css'
import 'ag-grid-community/dist/styles/ag-theme-alpine.css'
import { AgGridVue } from 'ag-grid-vue'
import Vue from 'vue'
import { mapActions } from 'vuex'
import { Message } from 'element-ui'
import TextSetFilter from './TextSetFilter.vue'

const TextSetFilterCtor = Vue.extend(TextSetFilter)

// Maps a breakdownType prop value to the OitmModule action that fetches it.
const BREAKDOWN_ACTIONS = {
    'ordered-purchase-order': 'getItemOrderedPurchaseOrderBreakdown',
    'sales-order': 'getItemSalesOrderBreakdown',
    'in-transit': 'getItemInTransitBreakdown'
}

const pickText = (field, headerName) => ({
    headerName,
    field,
    editable: false,
    minWidth: 120,
    filterFramework: TextSetFilterCtor,
    filterParams: {
        valuesGetter: ({ api }) => {
            const seen = Object.create(null)
            const add = (node) => {
                if (!node || !node.data) return
                const raw = node.data[field]
                const s = raw == null ? '' : String(raw)
                seen[s] = true
            }
            api.forEachNodeAfterFilter(add)
            if (!Object.keys(seen).length) api.forEachNode(add)
            const vals = Object.keys(seen)
            vals.sort((a, b) => {
                if (a === '' && b !== '') return -1
                if (b === '' && a !== '') return 1
                return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })
            })
            return vals
        }
    }
})

export default {
    name: 'InventoryByWarehouseCellDialog',
    components: {
        AgGridVue
    },
    props: {
        visible: {
            type: Boolean,
            required: true
        },
        row: {
            type: Object,
            default: null
        },
        clickedField: {
            type: String,
            default: null
        },
        clickedLabel: {
            type: String,
            default: ''
        },
        fields: {
            type: Array,
            default: () => []
        },
        columns: {
            type: Array,
            default: () => []
        },
        title: {
            type: String,
            default: 'Inventory Details'
        },
        breakdownType: {
            type: String,
            default: null,
            validator: (value) => value === null || Object.prototype.hasOwnProperty.call(BREAKDOWN_ACTIONS, value)
        }
    },
    data() {
        return {
            gridApi: null,
            columnApi: null,
            remoteRows: [],
            remoteLoading: false,
            remoteError: null,
            contextMenu: {
                visible: false,
                x: 0,
                y: 0,
                rowIndex: 0,
                header: '',
                field: null,
                row: null,
                definition: null
            }
        }
    },
    computed: {
        localVisible: {
            get() {
                return this.visible
            },
            set(val) {
                this.$emit('update:visible', val)
            }
        },
        dialogTitle() {
            if (!this.clickedLabel) return this.title
            return `${this.title} - ${this.clickedLabel}`
        },
        photoUrl() {
            const row = this.row
            if (!row) return ''
            if (Array.isArray(row.image) && row.image.length) {
                const i = row.image[0]
                return (typeof i === 'string' ? i : (i.large || i.url || i.thumb || i.small)) || ''
            }
            return row.thumb || (typeof row.image === 'string' ? row.image : '')
        },
        isRemoteBreakdown() {
            return Boolean(this.breakdownType)
        },
        remoteActionName() {
            return this.breakdownType ? BREAKDOWN_ACTIONS[this.breakdownType] : null
        },
        labelMap() {
            const map = {}
            if (Array.isArray(this.columns)) {
                this.columns.forEach((col) => {
                    if (col && col.field) map[col.field] = col.label || col.field
                })
            }
            return map
        },
        columnDefs() {
            return [
                {
                    ...pickText('label', 'Field'),
                    minWidth: 220
                },
                {
                    ...pickText('value', 'Value'),
                    minWidth: 120,
                    cellClass: 'ag-num'
                }
            ]
        },
        modelRowData() {
            if (!this.row || !Array.isArray(this.fields)) return []
            return this.fields.map((field) => {
                const value = this.row[field]
                return {
                    field: field,
                    label: this.labelMap[field] || field,
                    value: value === undefined || value === null ? 0 : value
                }
            }).filter((item) => Number(String(item.value).replace(/,/g, '')) > 0)
        },
        totalValue() {
            return this.modelRowData.reduce((sum, item) => {
                const normalized = String(item.value).replace(/,/g, '')
                const numeric = Number(normalized)
                return Number.isFinite(numeric) ? sum + numeric : sum
            }, 0)
        },
        pinnedBottomRowData() {
            if (!this.row) return []

            return [{
                field: '__pinned_total__',
                label: 'Total',
                value: this.totalValue
            }]
        },
        remoteColumnDefs() {
            const keys = []
            this.remoteRows.forEach(row => {
                Object.keys(row || {}).forEach(key => {
                    if (!keys.includes(key)) keys.push(key)
                })
            })
            return keys.map(key => {
                const hasLineBreaks = this.remoteRows.some(row =>
                    typeof row[key] === 'string' && /[\r\n]|\\r?\\n/.test(row[key])
                )
                const exactValue = params => {
                    const data = params && params.data
                    return data && Object.prototype.hasOwnProperty.call(data, key) ? data[key] : ''
                }

                if (!hasLineBreaks) {
                    return {
                        ...pickText(key, this.humanizeKey(key)),
                        minWidth: 140,
                        valueGetter: exactValue
                    }
                }

                return {
                    ...pickText(key, this.humanizeKey(key)),
                    minWidth: 240,
                    wrapText: true,
                    autoHeight: true,
                    valueGetter: exactValue,
                    cellRenderer: params => {
                        const value = params.value == null ? '' : String(params.value)
                        const cell = document.createElement('div')
                        cell.className = 'inventory-multiline-cell'
                        cell.textContent = value.replace(/\\r?\\n/g, '\n')
                        return cell
                    }
                }
            })
        },
        activeColumnDefs() {
            return this.isRemoteBreakdown ? this.remoteColumnDefs : this.columnDefs
        },
        activeRowData() {
            return this.isRemoteBreakdown ? this.remoteRows : this.modelRowData
        },
        activePinnedBottomRowData() {
            return this.isRemoteBreakdown ? [] : this.pinnedBottomRowData
        }
    },
    watch: {
        visible(val) {
            if (!val) {
                this.resetGridFilters()
                this.closeContextMenu()
            }
            if (val) {
                this.loadRemoteRows()
                this.$nextTick(() => {
                    if (this.gridApi) this.gridApi.sizeColumnsToFit()
                })
            }
        },
        modelRowData() {
            this.$nextTick(() => {
                if (this.gridApi) this.gridApi.sizeColumnsToFit()
            })
        },
        remoteRows() {
            this.$nextTick(() => {
                if (this.gridApi) this.gridApi.sizeColumnsToFit()
            })
        }
    },
    mounted() {
        document.addEventListener('click', this.closeContextMenu)
        document.addEventListener('keydown', this.handleContextMenuKeydown)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.closeContextMenu)
        document.removeEventListener('keydown', this.handleContextMenuKeydown)
    },
    methods: {
        ...mapActions('InventoryItemMasterModule', [
            'getItemOrderedPurchaseOrderBreakdown',
            'getItemSalesOrderBreakdown',
            'getItemInTransitBreakdown'
        ]),
        onGridReady(params) {
            this.gridApi = params.api
            this.columnApi = params.columnApi
            this.$nextTick(() => {
                if (this.gridApi) this.gridApi.sizeColumnsToFit()
            })
        },
        humanizeKey(key) {
            return String(key || '')
                .replace(/[_-]+/g, ' ')
                .replace(/\b\w/g, character => character.toUpperCase())
        },
        async loadRemoteRows() {
            this.remoteRows = []
            this.remoteError = null
            if (!this.remoteActionName || !this.row || !this.row.series) return

            this.remoteLoading = true
            try {
                const payload = await this[this.remoteActionName]({ series: this.row.series })
                const data = payload && payload.data !== undefined ? payload.data : payload
                this.remoteRows = Array.isArray(data)
                    ? data.filter(item => item && typeof item === 'object')
                    : (data && typeof data === 'object' ? [data] : [])
            } catch (error) {
                const response = error && error.response
                this.remoteError = response && response.data && response.data.message
                    ? response.data.message
                    : 'Unable to load inventory details.'
            } finally {
                this.remoteLoading = false
            }
        },
        getCopyRows() {
            const rows = []
            const api = this.gridApi || (this.$refs.model && this.$refs.model.api)

            if (api && typeof api.forEachNodeAfterFilterAndSort === 'function') {
                api.forEachNodeAfterFilterAndSort(node => {
                    if (node && node.data) rows.push(node.data)
                })
            } else if (Array.isArray(this.activeRowData)) {
                rows.push(...this.activeRowData)
            }

            return rows
        },
        getCopyColumns() {
            const columns = this.columnApi && typeof this.columnApi.getAllDisplayedColumns === 'function'
                ? this.columnApi.getAllDisplayedColumns()
                : this.activeColumnDefs.filter(column => !column.hide)

            return columns.map(column => {
                const definition = typeof column.getColDef === 'function'
                    ? (column.getColDef() || {})
                    : column
                const field = definition.field

                if (!field) return null

                return {
                    field: field,
                    header: definition.headerName || field,
                    definition: definition
                }
            }).filter(Boolean)
        },
        getCopyCellValue(row, column) {
            let value = row[column.field]
            const definition = column.definition

            if (definition && typeof definition.valueGetter === 'function') {
                try {
                    value = definition.valueGetter({ data: row, colDef: definition })
                } catch (e) {
                    // Keep the raw value when a grid value getter needs runtime-only context.
                }
            }

            if (value === null || value === undefined) return ''
            if (Array.isArray(value)) return value.map(item => this.copyCellValue(item)).join(', ')
            if (typeof value === 'object') return JSON.stringify(value)

            return String(value).replace(/[\r\n]+/g, ' ').replace(/\t/g, ' ')
        },
        buildCopyText(rows, columns, includeHeaders) {
            const values = rows.map(row => columns.map(column => this.getCopyCellValue(row, column)))
            const lines = values.map(row => row.join('\t'))

            if (includeHeaders) {
                lines.unshift(columns.map(column => this.copyCellValue(column.header)).join('\t'))
            }

            return lines.join('\n')
        },
        copyRowsToClipboard(rows, columns, successMessage, includeHeaders) {
            const text = this.buildCopyText(rows, columns, includeHeaders !== false)

            this.writeClipboardText(text).then(copied => {
                if (copied) {
                    Message.success({ message: successMessage })
                } else {
                    Message.error({ message: 'Unable to copy table data.' })
                }
            })
        },
        handleGridCellContextMenu(params) {
            if (!params || !params.node || !params.column) return

            if (params.event && typeof params.event.preventDefault === 'function') {
                params.event.preventDefault()
            }

            const definition = params.colDef || (params.column.getColDef && params.column.getColDef()) || {}
            const columnId = params.column.getColId()
            const field = definition.field || columnId
            const clientX = params.event && Number.isFinite(params.event.clientX) ? params.event.clientX : 8
            const clientY = params.event && Number.isFinite(params.event.clientY) ? params.event.clientY : 8
            const menuWidth = 220
            const menuHeight = 150

            this.contextMenu = {
                visible: true,
                x: Math.max(8, Math.min(clientX, window.innerWidth - menuWidth - 8)),
                y: Math.max(8, Math.min(clientY, window.innerHeight - menuHeight - 8)),
                rowIndex: this.getGridRowIndex(params.node),
                header: definition.headerName || columnId,
                field: field,
                row: params.node.data,
                definition: definition
            }
        },
        suppressBrowserContextMenu(event) {
            if (event && typeof event.preventDefault === 'function') {
                event.preventDefault()
            }
        },
        getGridRowIndex(node) {
            const rows = this.getCopyRows()
            const rowIndex = rows.indexOf(node && node.data)
            if (rowIndex >= 0) return rowIndex + 1
            if (node && Number.isFinite(node.rowIndex)) return node.rowIndex + 1
            return 0
        },
        closeContextMenu() {
            if (this.contextMenu.visible) this.contextMenu.visible = false
        },
        handleContextMenuKeydown(event) {
            if (event.key === 'Escape') this.closeContextMenu()
        },
        copyContextCell() {
            const context = this.contextMenu
            this.closeContextMenu()
            if (!context.row || !context.field) return

            this.copyRowsToClipboard([context.row], [{
                field: context.field,
                header: context.header,
                definition: context.definition
            }], 'Copied cell.', false)
        },
        copyContextColumn() {
            const context = this.contextMenu
            this.closeContextMenu()
            if (!context.field) return

            this.copyRowsToClipboard(this.getCopyRows(), [{
                field: context.field,
                header: context.header,
                definition: context.definition
            }], `Copied ${context.header} column.`)
        },
        copyContextRow() {
            const context = this.contextMenu
            this.closeContextMenu()
            if (!context.row) return

            this.copyRowsToClipboard([context.row], this.getCopyColumns(), `Copied row ${context.rowIndex}.`)
        },
        copyContextTable() {
            const rows = this.getCopyRows()
            const columns = this.getCopyColumns()
            this.closeContextMenu()

            if (!rows.length || !columns.length) {
                Message.warning({ message: 'There is no table data to copy.' })
                return
            }

            this.copyRowsToClipboard(rows, columns, `Copied ${rows.length} rows to clipboard.`)
        },
        copyCellValue(value) {
            if (value === null || value === undefined) return ''
            if (Array.isArray(value)) return value.map(item => this.copyCellValue(item)).join(', ')
            if (typeof value === 'object') return JSON.stringify(value)

            return String(value).replace(/[\r\n]+/g, ' ').replace(/\t/g, ' ')
        },
        writeClipboardText(text) {
            if (navigator.clipboard && window.isSecureContext) {
                return navigator.clipboard.writeText(text).then(() => true).catch(() => false)
            }

            return new Promise(resolve => {
                const textarea = document.createElement('textarea')
                textarea.value = text
                textarea.setAttribute('readonly', '')
                textarea.style.position = 'fixed'
                textarea.style.opacity = '0'
                document.body.appendChild(textarea)
                textarea.select()

                let copied = false
                try {
                    copied = document.execCommand('copy')
                } catch (e) {
                    copied = false
                }

                document.body.removeChild(textarea)
                resolve(copied)
            })
        },
        resetGridFilters() {
            if (!this.gridApi) return
            this.gridApi.setQuickFilter('')
            this.gridApi.setFilterModel(null)
            this.gridApi.onFilterChanged()
        },
        handleClose() {
            this.resetGridFilters()
            this.$emit('close')
        }
    }
}
</script>

<style scoped>
.item-header {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 12px;
}
.item-photo {
    flex: 0 0 120px;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background: #fafafa;
}
.item-photo img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}
.item-details {
    line-height: 1.7;
}
.item-name {
    font-size: 16px;
    font-weight: 600;
}
.item-label {
    display: inline-block;
    min-width: 60px;
    color: #909399;
}
</style>

<style>
.cell-dialog-grid .inventory-multiline-cell {
    width: 100%;
    padding: 4px 0;
    line-height: 1.4;
    white-space: pre-line;
    overflow-wrap: anywhere;
}
.cell-dialog-grid.ag-theme-alpine .ag-cell {
    border-right: 1px solid #d1d5db;
}
.cell-dialog-grid.ag-theme-alpine .ag-header-cell {
    border-right: 1px solid #d1d5db;
}
.cell-dialog-grid.ag-theme-alpine .ag-row {
    border-bottom: 1px solid #d1d5db;
}
.cell-dialog-context-menu {
    position: fixed;
    z-index: 3000;
    min-width: 220px;
    padding: 6px 0;
    background: #fff;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, .18);
}
.cell-dialog-context-menu-location {
    padding: 6px 14px;
    color: #909399;
    font-size: 11px;
    border-bottom: 1px solid #ebeef5;
}
.cell-dialog-context-menu button {
    display: block;
    width: 100%;
    padding: 8px 14px;
    color: #303133;
    font-size: 13px;
    text-align: left;
    background: transparent;
    border: 0;
    cursor: pointer;
}
.cell-dialog-context-menu button:hover {
    color: #409eff;
    background: #f5f7fa;
}
</style>
